import React, {
    useState,
    useEffect,
    useReducer,
    useContext,
} from "react";
import { toast } from "sonner";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import Typography from "@material-ui/core/Typography";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import SearchIcon from "@material-ui/icons/Search";

import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import BlockIcon from "@material-ui/icons/Block";
import Chip from "@material-ui/core/Chip";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CreateNotificameTemplateModal from "../../components/CreateNotificameTemplateModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { Select, FormControl, InputLabel, MenuItem } from "@material-ui/core";
import useWhatsApps from "../../hooks/useWhatsApps";
import { isOfficialWhatsappConnection } from "../../utils/whatsappHelpers";

const reducer = (state, action) => {
    if (action.type === "LOAD_TEMPLATES") {
        const templates = action.payload;
        const newTemplates = [];

        templates.forEach((template) => {
            const templateIndex = state.findIndex((t) => t.id === template.id);
            if (templateIndex !== -1) {
                state[templateIndex] = template;
            } else {
                newTemplates.push(template);
            }
        });

        return [...state, ...newTemplates];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
}));

const Templates = () => {
    const classes = useStyles();

    const { user } = useContext(AuthContext);
    const { whatsApps, loading: loadingWhatsApps } = useWhatsApps();
    
    // Filtrar apenas conexões oficiais (com tokens preenchidos)
    const officialWhatsApps = whatsApps.filter(isOfficialWhatsappConnection);

    const [loading, setLoading] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [templates, dispatch] = useReducer(reducer, []);
    const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [createModalOpen, setCreateModalOpen] = useState(false);

    useEffect(() => {
        dispatch({ type: "RESET" });
    }, [searchParam, selectedWhatsappId, statusFilter]);

    useEffect(() => {
        if (!selectedWhatsappId) {
            dispatch({ type: "RESET" });
            setLoading(false);
            return;
        }

        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchTemplates = async () => {
                try {
                    const { data } = await api.get("/templates/list", {
                        params: { whatsappId: selectedWhatsappId },
                    });
                    
                    // Mapear templates do Notificame (underscore) para formato local (camelCase)
                    const mappedTemplates = (data.templates || []).map(template => ({
                        id: template.id,
                        name: template.name,
                        parameterFormat: template.parameter_format,
                        components: template.components,
                        language: template.language,
                        status: template.status,
                        category: template.category,
                        createdAt: template.created_at,
                        updatedAt: template.updated_at,
                        isInUse: template.isInUse || false
                    }));

                    // Ordenar por status e id (PENDING primeiro)
                    const statusOrder = {
                        'PENDING': 1,
                        'PENDENTE': 1,
                        'APPROVED': 2,
                        'REJECTED': 3,
                        'AGUARDANDO_APROVACAO': 4
                    };

                    mappedTemplates.sort((a, b) => {
                        // Primeiro ordena por status
                        const statusA = statusOrder[a.status] || 999;
                        const statusB = statusOrder[b.status] || 999;
                        
                        if (statusA !== statusB) {
                            return statusA - statusB;
                        }
                        
                        // Se status for igual, ordena por id
                        return a.id.localeCompare(b.id);
                    });

                    // Filtrar por busca e status localmente
                    let filteredTemplates = mappedTemplates;

                    // Filtro por nome
                    if (searchParam) {
                        filteredTemplates = filteredTemplates.filter(t => 
                            t.name.toLowerCase().includes(searchParam.toLowerCase())
                        );
                    }

                    // Filtro por status
                    if (statusFilter) {
                        filteredTemplates = filteredTemplates.filter(t => 
                            t.status === statusFilter
                        );
                    }

                    dispatch({ type: "LOAD_TEMPLATES", payload: filteredTemplates });
                    setLoading(false);
                } catch (err) {
                    toastError(err);
                    setLoading(false);
                }
            };
            fetchTemplates();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, selectedWhatsappId, statusFilter]);

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleWhatsappChange = (event) => {
        setSelectedWhatsappId(event.target.value);
    };

    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
    };

    const handleOpenCreateModal = () => {
        setCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
    };

    const handleTemplateCreated = (template) => {
        // Forçar recarga dos templates
        const reloadTemplates = async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/templates/list", {
                    params: { whatsappId: selectedWhatsappId },
                });
                
                const mappedTemplates = (data.templates || []).map(template => ({
                    id: template.id,
                    name: template.name,
                    parameterFormat: template.parameter_format,
                    components: template.components,
                    language: template.language,
                    status: template.status,
                    category: template.category,
                    createdAt: template.created_at,
                    updatedAt: template.updated_at,
                    isInUse: template.isInUse || false
                }));

                // Ordenar por status e id (PENDING primeiro)
                const statusOrder = {
                    'PENDING': 1,
                    'PENDENTE': 1,
                    'APPROVED': 2,
                    'REJECTED': 3,
                    'AGUARDANDO_APROVACAO': 4
                };

                mappedTemplates.sort((a, b) => {
                    const statusA = statusOrder[a.status] || 999;
                    const statusB = statusOrder[b.status] || 999;
                    
                    if (statusA !== statusB) {
                        return statusA - statusB;
                    }
                    
                    return a.id.localeCompare(b.id);
                });

                let filteredTemplates = mappedTemplates;

                if (searchParam) {
                    filteredTemplates = filteredTemplates.filter(t => 
                        t.name.toLowerCase().includes(searchParam.toLowerCase())
                    );
                }

                if (statusFilter) {
                    filteredTemplates = filteredTemplates.filter(t => 
                        t.status === statusFilter
                    );
                }

                dispatch({ type: "LOAD_TEMPLATES", payload: filteredTemplates });
                setLoading(false);
            } catch (err) {
                toastError(err);
                setLoading(false);
            }
        };

        reloadTemplates();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "APPROVED":
                return <CheckCircleIcon style={{ color: "green" }} fontSize="small" />;
            case "AGUARDANDO_APROVACAO":
                return <CancelIcon style={{ color: "blue" }} fontSize="small" />;
            case "PENDING":
            case "PENDENTE":
                return <CancelIcon style={{ color: "orange" }} fontSize="small" />;
            case "REJECTED":
                return <BlockIcon style={{ color: "red" }} fontSize="small" />;
            default:
                return <CancelIcon style={{ color: "gray" }} fontSize="small" />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "APPROVED":
                return i18n.t("templates.constants.status.APPROVED");
            case "AGUARDANDO_APROVACAO":
                return i18n.t("templates.constants.status.AGUARDANDO_APROVACAO");
            case "PENDING":
            case "PENDENTE":
                return i18n.t("templates.constants.status.PENDENTE");
            case "REJECTED":
                return i18n.t("templates.constants.status.REJECTED");
            default:
                return status;
        }
    };

    const getCategoryText = (category) => {
        switch (category) {
            case "MARKETING":
                return i18n.t("templates.constants.category.MARKETING");
            case "UTILITY":
                return i18n.t("templates.constants.category.UTILITY");
            case "AUTHENTICATION":
                return i18n.t("templates.constants.category.AUTHENTICATION");
            default:
                return category;
        }
    };

    const getParameterFormatText = (parameterFormat) => {
        switch (parameterFormat) {
            case "POSITIONAL":
                return i18n.t("templates.constants.parameterFormat.POSITIONAL");
            case "NAMED":
                return i18n.t("templates.constants.parameterFormat.NAMED");
            default:
                return parameterFormat;
        }
    };

    const getLanguageText = (language) => {
        switch (language) {
            case "pt_BR":
                return i18n.t("templates.constants.language.pt_BR");
            case "en_US":
                return i18n.t("templates.constants.language.en_US");
            case "es_ES":
                return i18n.t("templates.constants.language.es_ES");
            default:
                return language;
        }
    };

    return (
        <MainContainer className={classes.mainContainer}>
            <CreateNotificameTemplateModal
                open={createModalOpen}
                onClose={handleCloseCreateModal}
                whatsappId={selectedWhatsappId}
                onSuccess={handleTemplateCreated}
            />
            
            <MainHeader>
                <Title>{i18n.t("templates.title")} ({templates.length})</Title>
                <MainHeaderButtonsWrapper>
                    <FormControl variant="outlined" style={{ minWidth: 200, marginRight: 10 }}>
                        <InputLabel>Conexão WhatsApp</InputLabel>
                        <Select
                            value={selectedWhatsappId}
                            onChange={handleWhatsappChange}
                            label="Conexão WhatsApp"
                            disabled={loadingWhatsApps}
                        >
                            <MenuItem value="">
                                <em>Selecione uma conexão</em>
                            </MenuItem>
                            {officialWhatsApps.map((whatsapp) => (
                                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                    {whatsapp.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" style={{ minWidth: 180, marginRight: 10 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            label="Status"
                            disabled={!selectedWhatsappId}
                        >
                            <MenuItem value="">
                                <em>Todos os status</em>
                            </MenuItem>
                            <MenuItem value="APPROVED">
                                {getStatusText("APPROVED")}
                            </MenuItem>
                            <MenuItem value="PENDING">
                                Pendente
                            </MenuItem>
                            <MenuItem value="REJECTED">
                                {getStatusText("REJECTED")}
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        placeholder={i18n.t("templates.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                        disabled={!selectedWhatsappId}
                    />
                    {selectedWhatsappId && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleOpenCreateModal}
                        >
                            Criar Novo Template
                        </Button>
                    )}
                </MainHeaderButtonsWrapper>
            </MainHeader>

            <Paper
                className={classes.mainPaper}
                variant="outlined"
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>
                                {i18n.t("templates.table.name")}
                            </TableCell>
                            <TableCell align="center">
                                {i18n.t("templates.table.category")}
                            </TableCell>
                            <TableCell align="center">
                                {i18n.t("templates.table.parameterFormat")}
                            </TableCell>
                            <TableCell align="center">
                                {i18n.t("templates.table.status")}
                            </TableCell>
                            <TableCell align="center">
                                {i18n.t("templates.table.language")}
                            </TableCell>
                            <TableCell align="center">
                                Em Uso
                            </TableCell>
                            <TableCell align="center">
                                {i18n.t("templates.table.actions")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {templates.map((template) => (
                                <TableRow key={template.id}>
                                    <TableCell style={{ paddingRight: 0 }}>
                                        <Avatar style={{ 
                                            backgroundColor: 
                                                template.status === "APPROVED" ? "green" : 
                                                template.status === "AGUARDANDO_APROVACAO" ? "blue" : 
                                                (template.status === "PENDING" || template.status === "PENDENTE") ? "orange" : "red" 
                                        }}>
                                            {template.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </TableCell>
                                    <TableCell>{template.name}</TableCell>
                                    <TableCell align="center">
                                        {getCategoryText(template.category)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {getParameterFormatText(template.parameterFormat)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            {getStatusIcon(template.status)}
                                            <span style={{ marginLeft: "5px" }}>
                                                {getStatusText(template.status)}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell align="center">
                                        {getLanguageText(template.language)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {template.isInUse ? (
                                            <Chip
                                                size="small"
                                                label="Em Uso"
                                                style={{
                                                    backgroundColor: "#4caf50",
                                                    color: "#fff"
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">
                                                -
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="caption" color="textSecondary">
                                            Gerenciado no Notificame
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton avatar columns={7} />}
                        </>
                    </TableBody>
                </Table>
                
                {!selectedWhatsappId && !loading && (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            Selecione uma Conexão WhatsApp
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Para visualizar os templates do Notificame, selecione uma conexão WhatsApp no dropdown acima.
                        </Typography>
                    </div>
                )}
                
                {selectedWhatsappId && templates.length === 0 && !loading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Typography variant="body2" color="textSecondary">
                            {i18n.t("templates.noData")}
                        </Typography>
                    </div>
                )}
            </Paper>
        </MainContainer>
    );
};

export default Templates;
